import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { io, Socket } from "socket.io-client";
import { useSelector } from "react-redux";

import { useGracePeriodAlert } from "../../hooks/connectivityHooks/useGracePeriodAlert";

const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL ||
  import.meta.env.VITE_API_BASE_URL ||
  "http://localhost:3000";

interface ConsultationSocketContextType {
  socket: Socket | null;
}

const ConsultationSocketContext =
  createContext<ConsultationSocketContextType>({
    socket: null,
  });

export const useConsultationSocket = () =>
  useContext(ConsultationSocketContext);

const UserGracePeriodAlert: React.FC = () => {
  const activeSession = useSelector(
    (state: any) => state.activeSession?.session,
  );

  const userCode =
    localStorage.getItem("user_code") || "";

  const sessionId =
    activeSession?.sessionId || "";

  const {
    isGracePeriodActive,
    disconnectedRole,
    disconnectedName,
    formattedRemainingTime,
  } = useGracePeriodAlert({
    sessionId,
    senderCode: userCode,
    senderType: "user",
  });

  /*
   * Grace-period UI:
   *
   * First 5 seconds:
   * Show the existing alert in the center.
   *
   * After 5 seconds:
   * Move the alert to the bottom so the rest of
   * the consultation UI remains usable.
   */
  const [
    isGracePeriodFooter,
    setIsGracePeriodFooter,
  ] = useState(false);

  useEffect(() => {
    if (!isGracePeriodActive) {
      setIsGracePeriodFooter(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setIsGracePeriodFooter(true);
    }, 5000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isGracePeriodActive]);

  if (!isGracePeriodActive) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-[9999] pointer-events-none flex px-4 ${
        isGracePeriodFooter
          ? "items-end justify-center pb-5"
          : "items-center justify-center"
      }`}
    >
      <div
        className={`pointer-events-auto bg-white shadow-2xl border border-gray-200 ${
          isGracePeriodFooter
            ? "w-auto max-w-md rounded-xl px-4 py-2"
            : "w-full max-w-md rounded-2xl p-6"
        }`}
      >
        {!isGracePeriodFooter ? (
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100">
              <span className="text-2xl">
                ⚠️
              </span>
            </div>

            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                Attention
              </h2>

              <p className="mt-2 text-gray-600">
              {disconnectedName || "The other participant"} has been disconnected.
              </p>

              <p className="mt-3 text-sm text-gray-500">
              The {disconnectedRole === "expert" ? "expert's" : "user's"} connection was temporarily lost.
              </p>

              <div className="mt-5 rounded-xl bg-gray-100 px-4 py-3 text-center">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Time left before automatic
                  disconnection
                </p>

                <p className="mt-1 font-mono text-3xl font-bold text-red-600">
                  {formattedRemainingTime}
                </p>
              </div>

              <p className="mt-4 text-center text-xs text-gray-400">
                The consultation will continue
                automatically if the participant
                reconnects.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-lg">
              ⚠️
            </span>

            <div>
              <p className="text-xs font-semibold text-gray-900">
                {disconnectedName ||
                  "Participant"}{" "}
                disconnected
              </p>

              <p className="text-[11px] text-gray-500">
                Waiting for reconnection
              </p>
            </div>

            <div className="ml-2 rounded-lg bg-gray-100 px-3 py-1 text-center">
              <p className="text-[9px] font-medium uppercase tracking-wide text-gray-500">
                Time left
              </p>

              <p className="font-mono text-lg font-bold leading-5 text-red-600">
                {formattedRemainingTime}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const ConsultationSocketProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [socket, setSocket] =
    useState<Socket | null>(null);

  const socketRef =
    useRef<Socket | null>(null);

  const lastUserCodeRef =
    useRef<string | null>(null);

  /*
   * Keep the latest active session available inside Socket.IO
   * connect/reconnect callbacks without recreating the socket.
   */
  const activeSession = useSelector(
    (state: any) => state.activeSession?.session,
  );

  const activeSessionRef =
    useRef(activeSession);

  useEffect(() => {
    activeSessionRef.current =
      activeSession;
  }, [activeSession]);

  /*
   * Create ONE persistent USER consultation socket.
   *
   * This provider lives above:
   *
   * /call/:expertCode
   * /video/:expertCode
   * /chat/:expertCode
   * /user/dashboard
   *
   * Therefore changing between those routes must NOT destroy
   * the Socket.IO connection.
   */
  useEffect(() => {
    /*
     * Expert connectivity is already owned by ExpertSocketProvider.
     *
     * Do not create a second socket for an expert.
     */
    const expertCode =
      localStorage.getItem("expertCode") ||
      localStorage.getItem("expert_code");

    if (expertCode) {
      return;
    }

    const userCode =
      localStorage.getItem("user_code");

    /*
     * No authenticated user yet.
     */
    if (!userCode) {
      return;
    }

    /*
     * Socket already exists for this user.
     *
     * IMPORTANT:
     * Do not recreate it just because the active session or route
     * changed.
     */
    if (
      socketRef.current &&
      lastUserCodeRef.current === userCode
    ) {
      return;
    }

    /*
     * If another user was previously authenticated in this
     * provider instance, remove that old socket first.
     */
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
      setSocket(null);
    }

    lastUserCodeRef.current =
      userCode;

    const newSocket = io(
      SOCKET_URL,
      {
        path: "/socket.io/",
        transports: ["websocket"],
        upgrade: false,

        /*
         * Socket.IO automatically attempts to reconnect after
         * temporary network/socket failures.
         *
         * The backend grace-period mechanism decides whether the
         * consultation should ultimately be closed.
         */
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: Infinity,

        query: {
          senderCode: userCode,
          senderType: "user",
        },
      },
    );

    socketRef.current =
      newSocket;

    setSocket(newSocket);

    /*
     * Join the currently active consultation.
     *
     * This runs on:
     * 1. Initial connection
     * 2. Socket.IO reconnection
     *
     * Therefore if the user temporarily loses the network and
     * reconnects during the 3-minute backend grace period,
     * the backend receives join_session again and can cancel
     * the active grace period.
     */
    const joinActiveSession = () => {
      const currentSession =
        activeSessionRef.current;

      if (
        !currentSession?.sessionId
      ) {
        return;
      }

      newSocket.emit(
        "join_session",
        {
          sessionId:
            currentSession.sessionId,
        },
      );
    };

    const handleConnect = () => {
      joinActiveSession();
    };

    const handleDisconnect = () => {
      /*
       * DO NOT end the consultation here.
       *
       * A disconnect may be temporary.
       *
       * Backend grace-period handling is responsible for starting
       * and eventually expiring the grace period.
       */
    };

    newSocket.on(
      "connect",
      handleConnect,
    );

    newSocket.on(
      "disconnect",
      handleDisconnect,
    );

    /*
     * If Socket.IO has already connected before listeners were
     * attached, join immediately.
     */
    if (newSocket.connected) {
      joinActiveSession();
    }

    /*
     * This effect is intentionally NOT dependent on:
     *
     * - location
     * - activeSession
     * - rtcToken
     * - channelName
     *
     * Those values must not destroy the persistent socket.
     */
    return () => {
      /*
       * Remove only the listeners created by this effect.
       *
       * DO NOT disconnect here.
       *
       * The provider itself owns the socket lifecycle.
       */
      newSocket.off(
        "connect",
        handleConnect,
      );

      newSocket.off(
        "disconnect",
        handleDisconnect,
      );
    };
  }, []);

  /*
   * The active session can change while the persistent socket is
   * already connected.
   *
   * When a new consultation becomes active, immediately join it.
   */
  useEffect(() => {
    const currentSocket =
      socketRef.current;

    if (
      !currentSocket ||
      !currentSocket.connected
    ) {
      return;
    }

    if (
      !activeSession?.sessionId
    ) {
      return;
    }

    currentSocket.emit(
      "join_session",
      {
        sessionId:
          activeSession.sessionId,
      },
    );
  }, [
    activeSession?.sessionId,
  ]);

  /*
   * Final cleanup.
   *
   * This happens only when the actual persistent provider is
   * unmounted, not when the user navigates between consultation
   * and dashboard routes.
   */
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      lastUserCodeRef.current =
        null;
    };
  }, []);

  return (
    <ConsultationSocketContext.Provider
      value={{ socket }}
    >
      <UserGracePeriodAlert />

      {children}
    </ConsultationSocketContext.Provider>
  );
};